import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Plus, Save, Trash2, Eye, EyeOff, GripVertical,
  ChevronUp, ChevronDown, X, FolderOpen
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const DEFAULT_CATEGORIES = [
  { id: 'vitella', name: 'Vitella', icon: '🥩', visible: true },
  { id: 'maiale', name: 'Maiale', icon: '🐖', visible: true },
  { id: 'pollo', name: 'Pollo', icon: '🐔', visible: true },
  { id: 'preparati', name: 'Preparati', icon: '🍔', visible: true },
  { id: 'salame', name: 'Salame', icon: '🥓', visible: true },
  { id: 'pane', name: 'Pane', icon: '🥖', visible: true },
  { id: 'vino', name: 'Vino', icon: '🍷', visible: true },
  { id: 'uova', name: 'Uova', icon: '🥚', visible: true }
];

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const makeId = name => String(name || '')
  .toLocaleLowerCase('it-IT')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 40);

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [productCounts, setProductCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', icon: '📦' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [settingsResponse, productsResponse] = await Promise.all([
        axios.get(`${API}/admin/settings`, headers()),
        axios.get(`${API}/admin/products`, headers())
      ]);

      const configured = Array.isArray(settingsResponse.data?.product_categories)
        && settingsResponse.data.product_categories.length
        ? settingsResponse.data.product_categories
        : DEFAULT_CATEGORIES;

      setCategories(configured.map((item, index) => ({
        id: item.id,
        name: item.name,
        icon: item.icon || '📦',
        visible: item.visible !== false,
        order: Number.isFinite(Number(item.order)) ? Number(item.order) : index
      })).sort((a, b) => a.order - b.order));

      const counts = {};
      for (const product of productsResponse.data || []) {
        counts[product.category] = (counts[product.category] || 0) + 1;
      }
      setProductCounts(counts);
    } catch (error) {
      toast.error('Impossibile caricare le categorie');
    } finally {
      setLoading(false);
    }
  };

  const normalizedCategories = useMemo(
    () => categories.map((item, index) => ({ ...item, order: index })),
    [categories]
  );

  const updateCategory = (index, field, value) => {
    setCategories(previous => previous.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [field]: value } : item
    )));
  };

  const moveCategory = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= categories.length) return;
    setCategories(previous => {
      const next = [...previous];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const addCategory = () => {
    const name = newCategory.name.trim();
    const id = makeId(name);
    if (!name || !id) {
      toast.error('Inserisci il nome della categoria');
      return;
    }
    if (categories.some(item => item.id === id)) {
      toast.error('Esiste già una categoria con questo nome');
      return;
    }
    setCategories(previous => [
      ...previous,
      { id, name, icon: newCategory.icon || '📦', visible: true, order: previous.length }
    ]);
    setNewCategory({ name: '', icon: '📦' });
    setShowNew(false);
  };

  const removeCategory = index => {
    const item = categories[index];
    const count = productCounts[item.id] || 0;
    if (count > 0) {
      toast.error(`Prima sposta i ${count} prodotti presenti in "${item.name}"`);
      return;
    }
    if (!window.confirm(`Eliminare la categoria "${item.name}"?`)) return;
    setCategories(previous => previous.filter((_, itemIndex) => itemIndex !== index));
  };

  const save = async () => {
    if (!categories.length) {
      toast.error('Deve rimanere almeno una categoria');
      return;
    }
    if (categories.some(item => !item.name.trim() || !item.id)) {
      toast.error('Controlla i nomi delle categorie');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        product_categories: normalizedCategories
      }, headers());
      toast.success('Categorie salvate');
      setCategories(normalizedCategories);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F4EE] text-[#2C2C2C]">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E7E0D6]">
        <div className="max-w-4xl mx-auto px-3 sm:px-5 py-3 flex items-center gap-3">
          <Link to="/admin/site-editor" className="w-10 h-10 rounded-xl bg-[#F2EEE7] flex items-center justify-center">
            <ArrowLeft size={20} />
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="font-serif text-xl sm:text-2xl font-bold">Categorie prodotti</h1>
            <p className="text-xs sm:text-sm text-[#6F675F]">
              Ordina, rinomina e nascondi le sezioni dell’app
            </p>
          </div>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="h-10 px-3 sm:px-4 rounded-xl bg-[#5D1822] text-white font-semibold flex items-center gap-2 disabled:opacity-60"
          >
            <Save size={18} />
            <span className="hidden sm:inline">{saving ? 'Salvataggio...' : 'Salva'}</span>
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-3 sm:px-5 py-4 sm:py-6">
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mb-4 text-sm text-amber-900">
          Nascondere una categoria non elimina i prodotti. Una categoria può essere eliminata soltanto quando non contiene prodotti.
        </div>

        {loading ? (
          <div className="py-16 text-center text-[#6F675F]">Caricamento categorie...</div>
        ) : (
          <div className="space-y-2">
            {categories.map((item, index) => (
              <article
                key={item.id}
                className={`bg-white rounded-2xl border border-[#E6DFD5] p-3 sm:p-4 ${
                  item.visible === false ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <GripVertical size={19} className="text-[#A69D92] flex-shrink-0" />

                  <input
                    type="text"
                    value={item.icon}
                    onChange={event => updateCategory(index, 'icon', event.target.value.slice(0, 4))}
                    className="w-12 h-11 text-center text-xl rounded-xl border border-[#DDD6CC]"
                    aria-label="Icona categoria"
                  />

                  <div className="min-w-0 flex-1">
                    <input
                      type="text"
                      value={item.name}
                      onChange={event => updateCategory(index, 'name', event.target.value)}
                      className="w-full h-11 px-3 rounded-xl border border-[#DDD6CC] font-semibold"
                      aria-label="Nome categoria"
                    />
                    <p className="mt-1 text-xs text-[#6F675F]">
                      {productCounts[item.id] || 0} prodotti · codice: {item.id}
                    </p>
                  </div>

                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => moveCategory(index, -1)}
                      disabled={index === 0}
                      className="w-8 h-8 rounded-lg bg-[#F2EEE7] flex items-center justify-center disabled:opacity-30"
                      aria-label="Sposta sopra"
                    >
                      <ChevronUp size={17} />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveCategory(index, 1)}
                      disabled={index === categories.length - 1}
                      className="w-8 h-8 rounded-lg bg-[#F2EEE7] flex items-center justify-center disabled:opacity-30"
                      aria-label="Sposta sotto"
                    >
                      <ChevronDown size={17} />
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => updateCategory(index, 'visible', item.visible === false)}
                    className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      item.visible === false ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                    }`}
                    title={item.visible === false ? 'Mostra categoria' : 'Nascondi categoria'}
                  >
                    {item.visible === false ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>

                  <button
                    type="button"
                    onClick={() => removeCategory(index)}
                    className="w-10 h-10 rounded-xl bg-red-100 text-red-700 flex items-center justify-center"
                    title="Elimina categoria"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowNew(true)}
          className="mt-4 w-full h-12 rounded-2xl border-2 border-dashed border-[#BEB5AA] bg-white font-semibold flex items-center justify-center gap-2"
        >
          <Plus size={19} />
          Nuova categoria
        </button>
      </main>

      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/60 p-3 flex items-end sm:items-center justify-center">
          <div className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-serif text-xl font-bold">Nuova categoria</h2>
                <p className="text-sm text-[#6F675F]">Scegli nome e icona</p>
              </div>
              <button type="button" onClick={() => setShowNew(false)} className="w-10 h-10 rounded-xl bg-[#F2EEE7] flex items-center justify-center">
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-[72px_1fr] gap-3">
              <label>
                <span className="block text-sm font-semibold mb-2">Icona</span>
                <input
                  type="text"
                  value={newCategory.icon}
                  onChange={event => setNewCategory(previous => ({ ...previous, icon: event.target.value.slice(0, 4) }))}
                  className="w-full h-12 text-center text-2xl rounded-xl border border-[#DDD6CC]"
                />
              </label>
              <label>
                <span className="block text-sm font-semibold mb-2">Nome</span>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={event => setNewCategory(previous => ({ ...previous, name: event.target.value }))}
                  className="w-full h-12 px-4 rounded-xl border border-[#DDD6CC]"
                  placeholder="Esempio: Gastronomia"
                />
              </label>
            </div>

            <button
              type="button"
              onClick={addCategory}
              className="mt-5 w-full h-12 rounded-xl bg-[#5D1822] text-white font-semibold flex items-center justify-center gap-2"
            >
              <FolderOpen size={18} />
              Crea categoria
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
